import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { arcadeOrigin } from '@/config/runtime-config';

/**
 * Arcade's API keys. A secret is only ever returned by create and regenerate,
 * and never again: the portal shows it once and keeps no copy.
 */

export interface ApiKey {
  id: string;
  name: string | null;
  secretPrefix: string | null;
  createdAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface ApiKeyCreated {
  key: ApiKey;
  secret: string;
}

export interface ApiRequest {
  id: string;
  requestId: string | null;
  method: string | null;
  path: string | null;
  status: number | null;
  errorCode: string | null;
  latencyMs: number | null;
  clientIp: string | null;
  userAgent: string | null;
  createdAt: string | null;
}

export interface ApiRequestPage {
  edges: { cursor: string; node: ApiRequest }[];
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
}

const KEY_FIELDS = gql`
  fragment ApiKeyFields on ApiKey {
    id
    name
    secretPrefix
    createdAt
    lastUsedAt
    revokedAt
  }
`;

export const API_KEYS: TypedDocumentNode<{ apiKeys: ApiKey[] }> = gql`
  query ApiKeys {
    apiKeys(includeRevoked: true) {
      ...ApiKeyFields
    }
  }
  ${KEY_FIELDS}
`;

export const API_KEY: TypedDocumentNode<
  { apiKey: ApiKey & { requests: ApiRequestPage } },
  { id: string; first: number; after?: string | null }
> = gql`
  query ApiKey($id: UUID!, $first: Int!, $after: String) {
    apiKey(id: $id) {
      ...ApiKeyFields
      requests(first: $first, after: $after) {
        edges {
          cursor
          node {
            id
            requestId
            method
            path
            status
            errorCode
            latencyMs
            clientIp
            userAgent
            createdAt
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
  ${KEY_FIELDS}
`;

export const CREATE_API_KEY: TypedDocumentNode<{ createApiKey: ApiKeyCreated }, { name: string }> =
  gql`
    mutation CreateApiKey($name: String!) {
      createApiKey(name: $name) {
        secret
        key {
          ...ApiKeyFields
        }
      }
    }
    ${KEY_FIELDS}
  `;

export const REGENERATE_API_KEY: TypedDocumentNode<
  { regenerateApiKey: ApiKeyCreated },
  { id: string }
> = gql`
  mutation RegenerateApiKey($id: UUID!) {
    regenerateApiKey(id: $id) {
      secret
      key {
        ...ApiKeyFields
      }
    }
  }
  ${KEY_FIELDS}
`;

export const REVOKE_API_KEY: TypedDocumentNode<{ revokeApiKey: ApiKey }, { id: string }> = gql`
  mutation RevokeApiKey($id: UUID!) {
    revokeApiKey(id: $id) {
      ...ApiKeyFields
    }
  }
  ${KEY_FIELDS}
`;

export function pingUrl(): string {
  return `${arcadeOrigin()}/v1/ping`;
}

export interface PingResult {
  ok: boolean;
  /** HTTP status, or null when the request never got an answer. */
  status: number | null;
  latencyMs: number;
  /** The response body, pretty-printed when it was JSON. */
  body: string;
  /** A one-line reading of the outcome. */
  summary: string;
}

/** Call `GET /v1/ping` with a secret, as an integration would. */
export async function ping(secret: string): Promise<PingResult> {
  const started = performance.now();
  const elapsed = () => Math.round(performance.now() - started);
  try {
    const response = await fetch(pingUrl(), { headers: { Authorization: `Bearer ${secret}` } });
    const text = await response.text();
    let parsed: { message?: string; error?: { code?: string; message?: string } } | null = null;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
    return {
      ok: response.ok,
      status: response.status,
      latencyMs: elapsed(),
      body: parsed ? JSON.stringify(parsed, null, 2) : text,
      summary: response.ok
        ? 'Your key works.'
        : (parsed?.error?.message ?? `The API answered ${response.status}.`),
    };
  } catch {
    return {
      ok: false,
      status: null,
      latencyMs: elapsed(),
      body: '',
      summary: 'The request did not reach the API.',
    };
  }
}
